import { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { tr } from '../lib/i18nHelpers';

// Gap reason modal (2026-10-22 spec §5.2) — asks WHY an invoice number is
// missing from the sales sequence, ported from the workflow-home-demo.html
// mockup (gapTitle / gapQ / gapVoided / gapCancelled / gapNotIssued / gapOther).
//
// This component only emits callbacks — the parent (workflow pages) wires the
// actual POST /invoices/number-gaps/:n/reason call. Reason codes match the
// backend z.enum('voided' | 'cancelled' | 'not_issued' | 'other') in
// api/src/routes/invoices.ts.
export type GapReasonCode = 'voided' | 'cancelled' | 'not_issued' | 'other';

export default function GapReasonModal({ gapNumber, onSave, onSkip, onClose }: {
  gapNumber: string;
  onSave: (reasonCode: GapReasonCode, note: string) => void;
  onSkip: () => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState<GapReasonCode | null>(null);
  const [note, setNote] = useState('');

  const OPTIONS: { code: GapReasonCode; label: string }[] = [
    { code: 'voided', label: tr('Voided', '已作廢', '已作废') },
    { code: 'cancelled', label: tr('Cancelled', '已取消', '已取消') },
    { code: 'not_issued', label: tr('Not yet issued', '未開出', '未开出') },
    { code: 'other', label: tr('Other', '其他', '其他') },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-card border rounded-xl shadow-2xl w-full max-w-[420px] mx-4 max-h-[85vh] overflow-y-auto p-5"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold">{tr('Skipped invoice number', '發票跳號', '发票跳号')}</h3>
              <p className="text-sm text-muted-foreground">
                {tr(
                  `${gapNumber} is missing from the invoice number sequence. What happened?`,
                  `${gapNumber} 於發票編號序列中缺失，原因是？`,
                  `${gapNumber} 于发票编号序列中缺失，原因是？`,
                )}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded shrink-0" aria-label={tr('Close', '關閉', '关闭')}>
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Reason options */}
        <div className="space-y-2 mb-4 text-sm">
          {OPTIONS.map(opt => {
            const isOther = opt.code === 'other';
            return (
              <label
                key={opt.code}
                className={`flex items-center gap-2 border rounded-md px-3 py-2 cursor-pointer hover:bg-muted/50 ${reason === opt.code ? 'bg-muted/50' : ''}`}
              >
                <input
                  type="radio"
                  name="gap-reason"
                  checked={reason === opt.code}
                  onChange={() => setReason(opt.code)}
                />
                <span>{opt.label}</span>
                {isOther && (
                  <input
                    type="text"
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    disabled={reason !== 'other'}
                    placeholder={tr('please specify', '請說明', '请说明')}
                    className="ml-auto border rounded px-2 py-1 text-xs w-36 disabled:opacity-50"
                  />
                )}
              </label>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex justify-between">
          <button onClick={onSkip} className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground">
            {tr('Skip for now', '略過', '略过')}
          </button>
          <button
            onClick={() => reason && onSave(reason, note.trim())}
            disabled={reason === null}
            className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {tr('Save reason', '儲存原因', '保存原因')}
          </button>
        </div>
      </div>
    </div>
  );
}
